import type { ComponentType, ReactNode } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import type { LucideProps } from 'lucide-react';
import { cn } from '@utils/Cn.js';

interface BreadcrumbItem {
  label: string;
  onClick?: () => void;
}

interface AppShellPageProps {
  icon?: ComponentType<LucideProps>;
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  hero?: ReactNode;
  /** Optional breadcrumb trail, shown above the title on tablet+ */
  breadcrumb?: BreadcrumbItem[];
  children: ReactNode;
  contentClassName?: string;
  /** If true, limits content to a narrower max-width (for detail/form pages) */
  narrow?: boolean;
}

export default function AppShellPage({
  icon: Icon,
  title,
  subtitle,
  actions,
  hero,
  breadcrumb,
  children,
  contentClassName,
  narrow = false,
}: AppShellPageProps) {
  return (
    <IonPage>
      <IonContent className="ion-no-padding">
        <div className="min-h-full bg-body-bg">
          {/* ── Sticky page header ── */}
          <div className="sticky top-0 z-20 border-b border-line/80 bg-surface/85 backdrop-blur-xl">
            <div
              className={cn(
                'mx-auto flex flex-col gap-3 px-4 py-4 sm:px-8 lg:px-10',
                narrow ? 'max-w-5xl' : 'max-w-7xl',
              )}
            >
              {/* Breadcrumb — tablet+ only */}
              {breadcrumb && breadcrumb.length > 0 && (
                <nav
                  className="hidden md:flex items-center gap-1.5 text-[11px] font-semibold text-ink-faint"
                  aria-label="Breadcrumb"
                >
                  {breadcrumb.map((crumb, i) => (
                    <span key={i} className="flex items-center gap-1.5">
                      {i > 0 && <span className="text-line-strong">›</span>}
                      {crumb.onClick ? (
                        <button
                          type="button"
                          onClick={crumb.onClick}
                          className="hover:text-ink transition-colors cursor-pointer"
                        >
                          {crumb.label}
                        </button>
                      ) : (
                        <span className="text-ink-soft">{crumb.label}</span>
                      )}
                    </span>
                  ))}
                </nav>
              )}

              {/* Title row */}
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  {Icon && (
                    <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] bg-slate text-white shadow-[0_6px_20px_rgba(15,118,110,0.25)] lg:h-12 lg:w-12 lg:rounded-[16px]">
                      <Icon size={18} strokeWidth={2} className="lg:hidden" />
                      <Icon size={20} strokeWidth={2} className="hidden lg:block" />
                    </div>
                  )}
                  <div className="min-w-0 text-left">
                    <h1 className="text-[1.2rem] sm:text-[1.3rem] lg:text-[1.6rem] font-black tracking-tight text-ink leading-tight truncate">
                      {title}
                    </h1>
                    {subtitle && (
                      <p className="mt-0.5 text-xs sm:text-sm text-ink-faint line-clamp-1 sm:line-clamp-none">
                        {subtitle}
                      </p>
                    )}
                  </div>
                </div>

                {/* Actions — always right-aligned */}
                {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
              </div>

              {/* Hero slot (search bar, tab pills, etc.) */}
              {hero}
            </div>
          </div>

          {/* ── Page body ── */}
          <div
            className={cn(
              'mx-auto px-4 pt-5 pb-28 sm:px-8 lg:px-10',
              /* pb-28 on mobile (above bottom bar), pb-12 on tablet+ */
              'md:pb-12',
              narrow ? 'max-w-5xl' : 'max-w-7xl',
              contentClassName,
            )}
          >
            {children}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
